import React, { useState, useEffect } from 'react';
import {
    Box,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    Button,
    Select,
    Checkbox,
    VStack,
    useToast,
    Text,
    Stack,
    useColorModeValue,
    Flex,
    Spinner,
    FormErrorMessage,
    Image,
    Progress,
    FormHelperText,
} from '@chakra-ui/react';
import ReactSelect from 'react-select';
import axiosClient from '../../axios-client';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { GrAdd } from 'react-icons/gr';
import { FaCloudUploadAlt } from 'react-icons/fa';
import Resizer from "react-image-file-resizer";
import { useStateContext } from '../../context/ContextProvider';
import { renameFile } from '../../services/renameFile';

const FileInput = ({
    label,
    onChange,
    value,
    isRequired,
    errorMessage,
    accept,
    width,
    height,
    helperText
}) => {
    const [touched, setTouched] = useState(false);
    const [preview, setPreview] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const toast = useToast();

    const handleBlur = () => {
        setTouched(true);
    };

    const resizeFile = (file) => {
        if (file.size > 520000) {
            return new Promise((resolve) => {
                Resizer.imageFileResizer(
                    file,
                    width,
                    height,
                    "JPEG",
                    90,
                    0,
                    (uri) => {
                        resolve(uri);
                    },
                    "blob"
                );
            });
        } else {
            return file;
        }
    };

    const simulateProgress = () => {
        setIsUploading(true);
        setUploadProgress(0);
        const interval = setInterval(() => {
            setUploadProgress(prev => {
                if (prev >= 100) {
                    clearInterval(interval);
                    setIsUploading(false);
                    return 100;
                }
                return prev + 10;
            });
        }, 200);
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const fileExtension = file.name.split('.').pop().toLowerCase();

        if (!["png", "jpg", "jpeg"].includes(fileExtension)) {
            toast({
                title: "Type de fichier invalide",
                description: "Veuillez télécharger une image au format PNG, JPG ou JPEG.",
                status: "warning",
                duration: 3000,
                variant: "left-accent",
                isClosable: true,
            });
            return;
        }

        simulateProgress();

        try {
            const resizedFile = await resizeFile(file);
            const previewUrl = URL.createObjectURL(resizedFile);
            setPreview(previewUrl);
            const fileName = file.name;
            const imageFile = new File([resizedFile], fileName, { type: resizedFile.type });
            onChange({ target: { files: [imageFile] } });
        } catch (err) {
            console.error("Error resizing image:", err);
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    const isInvalid = isRequired && touched && !value;

    return (
        <FormControl isRequired={isRequired} isInvalid={isInvalid} onBlur={handleBlur}>
            <FormLabel>{label}</FormLabel>
            {helperText && <FormHelperText mb={2}>{helperText}</FormHelperText>}
            <Flex direction="column" gap={2}>
                <Flex alignItems="center">
                    <Input
                        type="file"
                        display="none"
                        id={`file-input-${label}`}
                        onChange={handleFileChange}
                        accept={accept}
                    />
                    <Button
                        as="label"
                        htmlFor={`file-input-${label}`}
                        leftIcon={<FaCloudUploadAlt />}
                        colorScheme="cyan"
                        variant="outline"
                        borderRadius="md"
                        w="100%"
                        position="relative"
                        overflow="hidden"
                        _hover={{ bgGradient: 'linear(to-r, #29a4a6, #6ad7d9)', color: 'white' }}
                    >
                        <Box
                            position="absolute"
                            left="0"
                            top="0"
                            bottom="0"
                            width={`${uploadProgress}%`}
                            bg="cyan.100"
                            transition="width 0.2s"
                            opacity="0.3"
                        />
                        <Text position="relative">
                            {value ? value.name : 'Choisir un fichier'}
                        </Text>
                    </Button>
                    {preview && (
                        <Box ml={4}>
                            <Image
                                src={preview}
                                alt="file preview"
                                boxSize="50px"
                                objectFit="cover"
                                borderRadius="md"
                            />
                        </Box>
                    )}
                </Flex>
                {isUploading && (
                    <Flex align="center" gap={2}>
                        <Progress
                            value={uploadProgress}
                            size="xs"
                            colorScheme="cyan"
                            borderRadius="full"
                            flex="1"
                        />
                        <Text fontSize="sm" color="gray.500">
                            {uploadProgress}%
                        </Text>
                    </Flex>
                )}
            </Flex>
            {value && (
                <Text mt={2} fontSize="sm" color="gray.500">
                    {value.name}
                </Text>
            )}
            {isInvalid && (
                <FormErrorMessage>{errorMessage || 'Ce champ est requis'}</FormErrorMessage>
            )}
        </FormControl>
    );
};

const AddGame = () => {
    const { t } = useTranslation();
    const { user } = useStateContext();
    const navigate = useNavigate();
    const toast = useToast();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [gameUrl, setGameUrl] = useState('');
    const [image, setImage] = useState(null);
    const [status, setStatus] = useState('draft');
    // const [etat, setEtat] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);

    const [contents, setContents] = useState([]);
    const [selectedContents, setSelectedContents] = useState([]);

    const colorbg = useColorModeValue('white', '#171923');
    const txtcolor = useColorModeValue('black', 'white');
    const colorbghover = useColorModeValue('#e2e8f0', '#1e202b');

    // Fetch categories
    useEffect(() => {
        axiosClient.get('/categories/?page_size=1000&page=1')
            .then((res) => {
                setCategories(res.data.results);
            })
            .catch((err) => {
                toast({
                    title: 'Erreur lors du chargement des catégories.',
                    description: err.message,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            });
    }, []);

    // Fetch contents (movies/series)
    useEffect(() => {
        axiosClient.get('/movies/?page_size=1000&page=1')
            .then((res) => {
                setContents(res.data.results);
            })
            .catch((err) => {
                toast({
                    title: 'Erreur lors du chargement des contenus.',
                    description: err.message,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            });
    }, []);

    const validateUrl = (url) => {
        try {
            const urlObj = new URL(url);
            return urlObj.protocol === 'https:';
        } catch (e) {
            return false;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        // Validation
        if (!validateUrl(gameUrl)) {
            toast({
                title: 'URL invalide',
                description: 'L\'URL doit être une adresse HTTPS valide.',
                status: 'warning',
                duration: 4000,
                isClosable: true,
                position: "bottom-right",
                variant: "left-accent"
            });
            setIsLoading(false);
            return;
        }

        if (image?.name && image.name.length > 100) {
            toast({
                title: 'Erreur de validation.',
                description: 'Le nom de l\'image ne doit pas dépasser 100 caractères.',
                status: 'warning',
                duration: 4000,
                isClosable: true,
                position: "bottom-right",
                variant: "left-accent"
            });
            setIsLoading(false);
            return;
        }

        const formData = new FormData();
        formData.append('title', title);
        formData.append('description', description);
        formData.append('game_url', gameUrl);
        if (image) formData.append('image', renameFile(image));
        formData.append('status', status);
        // formData.append('etat', etat);

        selectedCategories.forEach((category) =>
            formData.append("category_ids", category.value)
        );

        selectedContents.forEach((content) =>
            formData.append("content_ids", content.value)
        );

        formData.append('edited_by', `${user.last_name} ${user.first_name}`);

        try {
            await axiosClient.post('/games/create/', formData);

            toast({
                description: 'Jeu ajouté avec succès.',
                status: 'success',
                duration: 3000,
                isClosable: true,
                position: "bottom-right",
                variant: "left-accent"
            });
            navigate('/jeux');
        } catch (error) {
            console.error(error);
            const errorMessage = error.response?.data?.message || "Une erreur s'est produite.";
            toast({
                description: errorMessage,
                status: 'error',
                duration: 5000,
                isClosable: true,
                variant: 'left-accent',
                position: "bottom-right"
            });
        } finally {
            setIsLoading(false);
        }
    };

    const isEmpty = () => {
        return !title.trim() || !description.trim() || !gameUrl.trim();
    };

    return (
        <Box>
            <Box w={"100%"} mt={3} mb={2}>
                <Text fontFamily='Plus Jakarta Display' as="cite" fontSize={{ base: "18px", md: "20px", lg: "25px" }} color="gray">
                    <span onClick={() => navigate('/')} style={{ cursor: 'pointer' }} onMouseEnter={e => e.target.style.textDecoration = "underline"} onMouseLeave={e => e.target.style.textDecoration = "none"}>
                        {t('Accueil')}
                    </span> / <span onClick={() => navigate('/jeux')} style={{ cursor: 'pointer' }} onMouseEnter={e => e.target.style.textDecoration = "underline"} onMouseLeave={e => e.target.style.textDecoration = "none"}>
                        {t('Liste des jeux')}
                    </span> / <span>{t('Ajouter un jeu')}</span>
                </Text>
            </Box>

            <Flex
                direction={{ base: 'column', md: 'row' }}
                align={{ base: 'stretch', md: 'center' }}
                justify={"center"}
                p={5}
            >
                <Stack
                    bg={useColorModeValue('white', 'gray.700')}
                    rounded={'lg'}
                    p={6}
                    my={5}
                    w={{ base: '100%', md: '100%', lg: '70%', sm: '100%' }}
                >
                    <Box p={5} rounded={'lg'} w="100%" maxW="full" justify={'center'}>
                        <Stack spacing={4} w={{ base: '100%', md: '100%', lg: '100%', sm: '100%' }}>
                            <FormControl isRequired>
                                <FormLabel>Titre du jeu</FormLabel>
                                <Input
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Entrez le titre du jeu"
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>Description</FormLabel>
                                <Textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Entrez une description du jeu"
                                    rows={4}
                                />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>URL du jeu (HTTPS)</FormLabel>
                                <Input
                                    value={gameUrl}
                                    onChange={(e) => setGameUrl(e.target.value)}
                                    placeholder="https://example.com/game"
                                    type="url"
                                />
                                <FormHelperText>
                                    L'URL doit commencer par https:// pour des raisons de sécurité
                                </FormHelperText>
                            </FormControl>

                            <FileInput
                                width={800}
                                height={600}
                                label="Image du jeu (vignette)"
                                value={image}
                                onChange={(e) => setImage(e.target.files[0])}
                                errorMessage="Veuillez télécharger une image."
                                accept=".png, .jpg, .jpeg"
                                helperText="Format recommandé: 800x600px"
                            />

                            <FormControl >
                                <FormLabel>Catégories</FormLabel>
                                <ReactSelect
                                    options={categories?.map((cat) => ({
                                        label: cat.name,
                                        value: cat.id,
                                    }))}
                                    value={selectedCategories}
                                    onChange={setSelectedCategories}
                                    isMulti
                                    closeMenuOnSelect={false}
                                    placeholder="Sélectionnez les catégories"
                                    styles={{
                                        container: (base) => ({
                                            ...base,
                                            width: '100%',
                                        }),
                                        control: (baseStyles, state) => ({
                                            ...baseStyles,
                                            border: '1px solid #CBD5E0',
                                            backgroundColor: 'transparent',
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            textAlign: 'left',
                                            backgroundColor: colorbg,
                                            cursor: "pointer"
                                        }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            color: txtcolor,
                                            backgroundColor: state.isSelected ? colorbg : 'inherit',
                                            '&:hover': {
                                                backgroundColor: colorbghover,
                                            },
                                            cursor: "pointer"
                                        }),
                                        singleValue: (provided) => ({
                                            ...provided,
                                            color: txtcolor
                                        }),
                                    }}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Contenus associés (Films/Séries)</FormLabel>
                                <ReactSelect
                                    options={contents?.map((content) => ({
                                        label: content.title,
                                        value: content.id,
                                    }))}
                                    value={selectedContents}
                                    onChange={setSelectedContents}
                                    isMulti
                                    closeMenuOnSelect={false}
                                    placeholder="Sélectionnez les contenus"
                                    styles={{
                                        container: (base) => ({
                                            ...base,
                                            width: '100%',
                                        }),
                                        control: (baseStyles, state) => ({
                                            ...baseStyles,
                                            border: '1px solid #CBD5E0',
                                            backgroundColor: 'transparent',
                                        }),
                                        menu: (provided) => ({
                                            ...provided,
                                            textAlign: 'left',
                                            backgroundColor: colorbg,
                                            cursor: "pointer"
                                        }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            color: txtcolor,
                                            backgroundColor: state.isSelected ? colorbg : 'inherit',
                                            '&:hover': {
                                                backgroundColor: colorbghover,
                                            },
                                            cursor: "pointer"
                                        }),
                                        singleValue: (provided) => ({
                                            ...provided,
                                            color: txtcolor
                                        }),
                                    }}
                                />
                                <FormHelperText>
                                    Optionnel: Associez ce jeu à des films ou séries
                                </FormHelperText>
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel>Statut</FormLabel>
                                <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                                    <option value="draft">Brouillon</option>
                                    <option value="published">Publié</option>
                                </Select>
                                <FormHelperText>
                                    Les jeux en brouillon ne sont pas visibles sur la plateforme
                                </FormHelperText>
                            </FormControl>

                            {/* <FormControl>
                                <Checkbox
                                    isChecked={etat}
                                    onChange={(e) => setEtat(e.target.checked)}
                                >
                                    Activer le jeu
                                </Checkbox>
                            </FormControl> */}

                            <Stack direction={['column', 'row']} spacing={10} pt={2} justifyContent="end">
                                <Button
                                    variant="outline"
                                    onClick={() => navigate('/jeux')}
                                >
                                    Annuler
                                </Button>
                                <Button
                                    type="submit"
                                    onClick={handleSubmit}
                                    colorScheme="green"
                                    isDisabled={isEmpty() || isLoading}
                                    leftIcon={!isLoading && <GrAdd />}
                                >
                                    {isLoading ? (
                                        <Flex align="center" gap={2}>
                                            <Spinner size="sm" />
                                            <Text>Chargement...</Text>
                                        </Flex>
                                    ) : "Ajouter"}
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                </Stack>
            </Flex>
        </Box>
    );
};

export default AddGame;